import { Box } from "arkh";
import React, { useCallback, useLayoutEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { HeaderButton } from "../../../components";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { TeamIconCircle } from "../../../components/mobile/TeamIconCircle";
import { KPISubTeamNameInput } from "../components/KPISubTeamNameInput";
import { KPICreateSubTeamScreenProps } from "./KPICreateSubTeamStack";
import { KPICreateSubTeamSetNameQuery } from "./__generated__/KPICreateSubTeamSetNameQuery.graphql";

import { Platform } from "react-native";
import cuid from "cuid";

const KPI_CREATE_SUB_TEAM_SET_NAME_QUERY = graphql`
  query KPICreateSubTeamSetNameQuery($teamId: ID!) {
    store {
      team(id: $teamId) {
        id
        icon
        color
        name
        ...TeamIconCircleComponent_Team
      }
    }
  }
`;

export function KPICreateSubTeamSetName(
  props: KPICreateSubTeamScreenProps<"KPICreateSubTeamSetName">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerKPICreateSubTeamSetName {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerKPICreateSubTeamSetName({
  route: { params },
  fetchKey,
  navigation,
}: KPICreateSubTeamScreenProps<"KPICreateSubTeamSetName"> & {
  fetchKey: string;
}) {
  const data = useLazyLoadQuery<KPICreateSubTeamSetNameQuery>(
    KPI_CREATE_SUB_TEAM_SET_NAME_QUERY,
    {
      teamId: params.teamId,
    },
    {
      fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const onCreate = useCallback(() => {
    if (params.type === "Edit") {
      const removeSections = [
        ...params.removeSections,
        ...params.remoteSections.flatMap((rs) =>
          rs.title === null && params.teamId === rs.assignedTeam.id
            ? [rs.id]
            : []
        ),
      ];
      navigation.navigateDeprecated("KPIEdit", {
        screen: "KPIEditSections",
        params: {
          routineId: params.routineId,
          title: params.title,
          localGoalDirection: params.localGoalDirection,
          localGoalsActive: params.localGoalsActive,
          localSections: [
            ...params.localSections.filter(
              (ls) =>
                !(ls.title === null && params.teamId === ls.assignedTeam.id)
            ),
            {
              id: cuid(),
              title: params.name,
              goalValue: 0,
              assignedTeam: { id: params.teamId },
            },
          ],
          removeSections,
        },
      });
    } else if (params.type === "Create") {
      const team = data.store.team!;
      if (
        params.teamGroups!.findIndex(
          ({ teamId }) => teamId === params.teamId
        ) !== -1
      ) {
        const teamGroups = params.teamGroups!.map((teamGroup) =>
          teamGroup.teamId === params.teamId
            ? {
                ...teamGroup,
                subTeams: [
                  ...teamGroup.subTeams,
                  { name: params.name, goalValue: 0 },
                ],
              }
            : teamGroup
        );
        navigation.navigateDeprecated("KPICreate", {
          ...params,
          teamGroups,
        });
      } else {
        const teamGroups = [
          ...params.teamGroups!,
          {
            teamId: team.id,
            color: team.color,
            icon: team.icon,
            name: team.name,
            goalValue: 0,
            subTeams: [
              {
                goalValue: 0,
                name: params.name,
              },
            ],
          },
        ];
        navigation.navigateDeprecated("KPICreate", {
          ...params,
          teamGroups,
        });
      }
    }
  }, [data.store.team, navigation, params]);
  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft:
        Platform.OS === "ios" && navigation.getState().routes.length === 1
          ? () => <HeaderButton type="CANCEL" onPress={navigation.goBack} />
          : undefined,
      headerRight: () => <HeaderButton type="CREATE" onPress={onCreate} />,
    });
  }, [navigation, onCreate]);

  return (
    <Box style={{ flex: 1 }} space="medium">
      <Box style={{ flexDirection: "row", alignItems: "center" }}>
        <TeamIconCircle team={data.store.team!} />
        <Box spaceRight="medium" />
        <KPISubTeamNameInput
          name={params.name}
          setName={(name) => navigation.setParams({ name })}
        />
      </Box>
    </Box>
  );
}
